"use server";

import { auth } from "../../lib/auth";
import { castLCKVote } from "../../lib/tournament";
import { revalidatePath } from "next/cache";
import { prisma } from "../../lib/prisma";
import { wrapEmail } from "../../lib/email";
import { dispatchNotification } from "../../lib/notify";
import { dbg } from "../../lib/debug-log";

// Get LCK voters for a name (tap-to-reveal info panel)
export async function getLCKVoters(
  nameId: string
): Promise<Array<{ userId: string; user: { displayName: string | null; role: string } }>> {
  const session = await auth();
  if (!session) return [];
  const votes = await prisma.lCKVote.findMany({
    where: { nameId },
    include: { user: { select: { displayName: true, role: true } } },
    orderBy: { createdAt: "asc" },
  });
  dbg.debug("LCK-action", "getLCKVoters", { nameId, count: votes.length });
  return votes.map((v) => ({
    userId: v.userId,
    user: { displayName: v.user.displayName, role: v.user.role },
  }));
}

// ── Resurrection email copy — pick one at random ──────────────────────────────
const RESURRECTION_VARIANTS: Array<{
  subject: (name: string) => string;
  body: (name: string) => string;
}> = [
  {
    subject: (name) => `🔥 ${name} just cooked their way back — Last Chance Kitchen update`,
    body: (name) => `
      <h2 style="color:#92400e;font-size:22px;margin:0 0 4px;">Down but never out.</h2>
      <p style="color:#78350f;font-size:15px;line-height:1.6;margin:0 0 20px;">
        They said it was over. They cleared the locker room. They were already writing the eulogy.<br><br>
        Then <strong>${name}</strong> walked back into the kitchen, tied on an apron, and
        cooked the most jaw-dropping comeback dish this tournament has ever seen.
        The crowd lost their minds. The judges couldn't look away. And the votes? <em>Unanimous.</em>
      </p>
      <div style="background:linear-gradient(135deg,#fef3c7,#fde68a);border-radius:14px;padding:20px 24px;text-align:center;margin-bottom:24px;">
        <div style="font-size:36px;margin-bottom:6px;">🔥</div>
        <div style="font-size:24px;font-weight:900;color:#92400e;letter-spacing:1px;">${name}</div>
        <div style="font-size:12px;font-weight:700;color:#b45309;text-transform:uppercase;letter-spacing:2px;margin-top:4px;">Is back in the game</div>
      </div>
      <p style="color:#6b7280;font-size:13px;">
        Head to the tournament to see how they stack up now that they've got a second shot.
      </p>
    `,
  },
  {
    subject: (name) => `🍳 The kitchen has spoken — ${name} rises from the ashes`,
    body: (name) => `
      <h2 style="color:#92400e;font-size:22px;margin:0 0 4px;">You can't keep a great name down.</h2>
      <p style="color:#78350f;font-size:15px;line-height:1.6;margin:0 0 20px;">
        Eliminated. Counted out. Left to simmer on the back burner.<br><br>
        But <strong>${name}</strong> refused to let the fire go out. They got back in that kitchen,
        turned up the heat, and plated something so spectacular the whole family had to vote them back in.
        Some names just have that <em>thing</em> — and tonight, we all remembered why.
      </p>
      <div style="background:linear-gradient(135deg,#fff7ed,#fed7aa);border-radius:14px;padding:20px 24px;text-align:center;margin-bottom:24px;">
        <div style="font-size:36px;margin-bottom:6px;">🏆</div>
        <div style="font-size:24px;font-weight:900;color:#9a3412;letter-spacing:1px;">${name}</div>
        <div style="font-size:12px;font-weight:700;color:#c2410c;text-transform:uppercase;letter-spacing:2px;margin-top:4px;">Officially Resurrected</div>
      </div>
      <p style="color:#6b7280;font-size:13px;">
        The tournament continues — and now ${name} is back in the mix. Cast your vote in the arena!
      </p>
    `,
  },
  {
    subject: (name) => `⚡ COMEBACK ALERT — ${name} just earned a second chance`,
    body: (name) => `
      <h2 style="color:#92400e;font-size:22px;margin:0 0 4px;">One last chance. That's all they needed.</h2>
      <p style="color:#78350f;font-size:15px;line-height:1.6;margin:0 0 20px;">
        The elimination felt final. The scoreboard said it was over. But the family 
        had other ideas — and <strong>${name}</strong> didn't waste a single vote of that second life.<br><br>
        They stepped back into the kitchen like they had a point to prove. Turns out,
        they had <em>several</em>. Tonight the phoenix flies.
      </p>
      <div style="background:linear-gradient(135deg,#fdf4ff,#f3e8ff);border-radius:14px;padding:20px 24px;text-align:center;margin-bottom:24px;">
        <div style="font-size:36px;margin-bottom:6px;">🐦‍🔥</div>
        <div style="font-size:24px;font-weight:900;color:#7e22ce;letter-spacing:1px;">${name}</div>
        <div style="font-size:12px;font-weight:700;color:#9333ea;text-transform:uppercase;letter-spacing:2px;margin-top:4px;">Phoenix Mode: Activated</div>
      </div>
      <p style="color:#6b7280;font-size:13px;">
        The bracket just got a lot more interesting. Go see how ${name} stacks up against the field.
      </p>
    `,
  },
  {
    subject: (name) => `🍳 Last Chance Kitchen delivers — ${name} is BACK`,
    body: (name) => `
      <h2 style="color:#92400e;font-size:22px;margin:0 0 4px;">The audacity. The grit. The flavor.</h2>
      <p style="color:#78350f;font-size:15px;line-height:1.6;margin:0 0 20px;">
        Every great comeback story needs a moment. A turning point. A dish so
        undeniably good that the room goes quiet, then erupts.<br><br>
        <strong>${name}</strong> just had that moment. Sent home too early, fueled by nothing
        but spite and the love of a family who wasn't ready to let go — they cooked 
        like their life depended on it. Because it did. And they <em>nailed it.</em>
      </p>
      <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);border-radius:14px;padding:20px 24px;text-align:center;margin-bottom:24px;">
        <div style="font-size:36px;margin-bottom:6px;">✨</div>
        <div style="font-size:24px;font-weight:900;color:#065f46;letter-spacing:1px;">${name}</div>
        <div style="font-size:12px;font-weight:700;color:#047857;text-transform:uppercase;letter-spacing:2px;margin-top:4px;">Back in the Championship</div>
      </div>
      <p style="color:#6b7280;font-size:13px;">
        ${name} re-enters the active roster. The tournament rages on — head to the arena to see what happens next.
      </p>
    `,
  },
  {
    subject: (name) => `🔥 They're not done yet — ${name} survives the Last Chance Kitchen`,
    body: (name) => `
      <h2 style="color:#92400e;font-size:22px;margin:0 0 4px;">Eliminated. Motivated. Undefeated spirit.</h2>
      <p style="color:#78350f;font-size:15px;line-height:1.6;margin:0 0 20px;">
        The bracket sent them packing, but the kitchen? The kitchen had mercy.<br><br>
        With the family's votes stacking up and the clock ticking, <strong>${name}</strong>
        channeled every loss, every near-miss, every "maybe next time" into something
        unstoppable. The Last Chance Kitchen has seen plenty of survivors. 
        Rarely one this hungry.
      </p>
      <div style="background:linear-gradient(135deg,#fffbeb,#fef3c7);border-radius:14px;padding:20px 24px;text-align:center;margin-bottom:24px;">
        <div style="font-size:36px;margin-bottom:6px;">💥</div>
        <div style="font-size:24px;font-weight:900;color:#92400e;letter-spacing:1px;">${name}</div>
        <div style="font-size:12px;font-weight:700;color:#b45309;text-transform:uppercase;letter-spacing:2px;margin-top:4px;">Survived · Thrived · Revived</div>
      </div>
      <p style="color:#6b7280;font-size:13px;">
        The tournament roster just got one name longer — and one comeback story richer.
      </p>
    `,
  },
];

function getResurrectionEmail(name: string, commentary?: string): { subject: string; text: string; html: string } {
  const variant = RESURRECTION_VARIANTS[Math.floor(Math.random() * RESURRECTION_VARIANTS.length)];
  const subject = variant.subject(name);
  const bodyHtml = variant.body(name);
  const commentaryBlock = commentary
    ? `<div style="background:#f5f3ff;border-left:4px solid #7c3aed;border-radius:0 8px 8px 0;padding:14px 18px;margin:0 0 20px;">
        <div style="font-size:11px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:6px;">🎙 The Commentators Say</div>
        <div style="font-size:15px;color:#4c1d95;font-style:italic;line-height:1.5;">&ldquo;${commentary}&rdquo;</div>
      </div>`
    : "";
  const html = wrapEmail(`
    ${bodyHtml}
    ${commentaryBlock}
    <div style="text-align:center;margin-top:24px;">
      <a href="${process.env.NEXTAUTH_URL ?? "https://baby.toonamo.org"}/vote"
        style="display:inline-block;background:#7c3aed;color:white;padding:14px 28px;border-radius:12px;text-decoration:none;font-weight:900;font-size:15px;">
        Go to the Arena &rarr;
      </a>
    </div>
  `);
  const text = `${name} has been resurrected via Last Chance Kitchen and is back in the Baby Name Championship!${commentary ? ` "${commentary}"` : ""} Head to ${process.env.NEXTAUTH_URL ?? "https://baby.toonamo.org"}/vote to cast your vote.`;
  return { subject, text, html };
}

// ── Cast LCK vote ─────────────────────────────────────────────────────────────
export async function castLCKVoteAction(nameId: string): Promise<{
  success: boolean;
  resurrected?: boolean;
  error?: string;
}> {
  const session = await auth();
  if (!session) return { success: false, error: "Not authenticated" };

  dbg.info("LCK-action", `castLCKVoteAction called`, { userId: session.user.id, nameId });

  let result: Awaited<ReturnType<typeof castLCKVote>>;
  try {
    result = await castLCKVote(session.user.id, nameId);
  } catch (err) {
    dbg.error("LCK-action", "castLCKVote threw an unexpected error", { err: String(err), userId: session.user.id, nameId });
    return { success: false, error: "An unexpected error occurred — check the debug log for details." };
  }

  if (result.success) {
    revalidatePath("/bracket");
    revalidatePath("/lck");
    revalidatePath("/names");

    // Dispatch resurrection notification per recipient's email/push/both/none preference
    if (result.resurrected) {
      const name = await prisma.name.findUnique({ where: { id: nameId }, select: { name: true, lckCommentary: true } });
      if (name) {
        const { subject, text, html } = getResurrectionEmail(name.name, name.lckCommentary ?? undefined);
        dispatchNotification({
          type: "lckResurrection",
          recipientWhere: {},
          email: { subject, text, html },
          push: {
            title: `🔥 ${name.name} is back!`,
            body:  `The family voted ${name.name} back into the Baby Name Championship via Last Chance Kitchen!`,
            url:   "/lck",
            tag:   "bnc-lck-resurrection",
          },
        }).catch((err) => dbg.error("LCK-action", "dispatchNotification (resurrection) threw", { error: String(err) }));
      }
    }
  }

  return result;
}

// ── Undo LCK vote ─────────────────────────────────────────────────────────────
export async function undoLCKVoteAction(nameId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  const session = await auth();
  if (!session) return { success: false, error: "Not authenticated" };

  dbg.info("LCK-action", `undoLCKVoteAction called`, { userId: session.user.id, nameId });

  // Safety checks
  const name = await prisma.name.findUnique({ where: { id: nameId } });
  if (!name) {
    dbg.warn("LCK-action", `undoLCKVoteAction — name not found`, { nameId });
    return { success: false, error: "Name not found" };
  }

  // Can't undo if the name has already been resurrected — the threshold was met
  if (name.resurrected) {
    dbg.warn("LCK-action", `undoLCKVoteAction blocked — name already resurrected`, { nameId, name: name.name, userId: session.user.id });
    return { success: false, error: "This name has already been resurrected — vote cannot be undone" };
  }

  const existing = await prisma.lCKVote.findUnique({
    where: { userId_nameId: { userId: session.user.id, nameId } },
  });
  if (!existing) {
    dbg.warn("LCK-action", `undoLCKVoteAction — no vote to undo`, { userId: session.user.id, nameId });
    return { success: false, error: "No vote found to undo" };
  }

  await prisma.$transaction([
    prisma.lCKVote.delete({ where: { userId_nameId: { userId: session.user.id, nameId } } }),
    prisma.name.update({ where: { id: nameId }, data: { lcKVoteCount: { decrement: 1 } } }),
  ]);

  dbg.info("LCK-action", `undoLCKVoteAction complete`, { userId: session.user.id, nameId, name: name.name });

  revalidatePath("/bracket");
  revalidatePath("/lck");
  revalidatePath("/names");

  return { success: true };
}
